import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for detecting double-tap gestures with haptic feedback
 * @param {Function} onDoubleTap - Callback function to execute on double-tap
 * @param {number} delay - Maximum delay between taps in milliseconds (default: 300ms)
 * @returns {Object} - Event handlers to attach to element
 */
export const useDoubleTap = (onDoubleTap, delay = 300) => {
  const [lastTap, setLastTap] = useState(0);
  const tapTimeoutRef = useRef(null);

  const triggerHaptic = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate(15); // Short vibration for double-tap
    }
  }, []);

  const handleTap = useCallback((event) => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTap;

    if (timeSinceLastTap < delay && timeSinceLastTap > 0) {
      // Double-tap detected
      event?.preventDefault();
      event?.stopPropagation();
      triggerHaptic();
      onDoubleTap?.(event);
      setLastTap(0); // Reset
      if (tapTimeoutRef?.current) {
        clearTimeout(tapTimeoutRef?.current);
        tapTimeoutRef.current = null;
      }
    } else {
      // First tap
      setLastTap(now);
      // Clear previous timeout
      if (tapTimeoutRef?.current) {
        clearTimeout(tapTimeoutRef?.current);
      }
      // Set timeout to reset if no second tap
      tapTimeoutRef.current = setTimeout(() => {
        setLastTap(0);
      }, delay);
    }
  }, [lastTap, delay, onDoubleTap, triggerHaptic]);

  useEffect(() => {
    return () => {
      if (tapTimeoutRef?.current) {
        clearTimeout(tapTimeoutRef?.current);
      }
    };
  }, []);

  return {
    onTouchEnd: handleTap,
    onClick: handleTap // Fallback for mouse clicks
  };
};

/**
 * Custom hook for detecting long-press gestures with haptic feedback
 * @param {Function} onLongPress - Callback function to execute on long-press
 * @param {number} duration - Duration in milliseconds to trigger long-press (default: 500ms)
 * @returns {Object} - Event handlers to attach to element
 */
export const useLongPress = (onLongPress, duration = 500) => {
  const timeoutRef = useRef(null);
  const isLongPressRef = useRef(false);
  const hasVibratedRef = useRef(false);

  const triggerHaptic = useCallback(() => {
    if ('vibrate' in navigator && !hasVibratedRef?.current) {
      navigator.vibrate(20); // Medium vibration for long-press
      hasVibratedRef.current = true;
    }
  }, []);

  const start = useCallback((event) => {
    isLongPressRef.current = false;
    hasVibratedRef.current = false;

    timeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      triggerHaptic();
      onLongPress?.(event);
    }, duration);
  }, [duration, onLongPress, triggerHaptic]);

  const clear = useCallback(() => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
      timeoutRef.current = null;
    }
    hasVibratedRef.current = false;
  }, []);

  const handleClick = useCallback((event) => {
    // Prevent click if it was a long-press
    if (isLongPressRef?.current) {
      event?.preventDefault();
      event?.stopPropagation();
    }
    isLongPressRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
      }
    };
  }, []);

  return {
    onMouseDown: start,
    onMouseUp: clear,
    onMouseLeave: clear,
    onTouchStart: start,
    onTouchEnd: clear,
    onTouchCancel: clear,
    onClick: handleClick
  };
};